// release-flow-resume-observability.ts
//
// P24 (found while measuring P23). `maybeResumeReleaseFlowAfterDone`
// computes what the release flow did — parked on a review report, waiting on
// G3, blocked on a deploy — and until now that answer was assigned to a
// field NOBODY read: grep gave three hits (the assignment, the return, the
// type declaration) and zero reads. So a mission stuck on
// `waiting_review_report` printed the SAME line as one that finished:
// `outcome=done sau N tick`. Measured in P23: that line appeared three times
// in a row while the flow was parked, first on a missing PAT and then on a
// stale review-report SHA.
//
// This is the project's third instance of one shape — P20's Lỗi A mapped a
// real `gate_blocked` onto a bland `dispatched` and the operator saw `idle`;
// P21 awaited the promotion result and dropped it. A state the operator must
// act on cannot be indistinguishable from success.
//
// Two outputs on purpose, because they serve different readers: a printed
// line for the person watching the run, and a journal event so `status
// --json` and any later investigation can still see it once scrollback is
// gone. Printing alone would repeat the mistake in a quieter way.
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume.js";

/**
 * A mission whose ticket loop finished is settled only when nothing
 * downstream still waits on a person or failed: no release configured,
 * released (now or on an earlier run), or — H14 — G3 approved with no
 * deploy_recipe (`deploy_not_configured`: done without deploy, by config).
 * Settled ⇔ `run` exits 0 and prints the archive hint (D15).
 */
export function isReleaseFlowSettled(outcome: ReleaseFlowResumeOutcome | undefined): boolean {
  return (
    outcome === undefined ||
    outcome.kind === "skipped_no_release_config" ||
    outcome.kind === "released" ||
    outcome.kind === "already_deployed" ||
    outcome.kind === "deploy_not_configured"
  );
}

/**
 * H14 (2026-09-29): the `mission_status_changed` label for a ticket loop that
 * reached `done`, one per honest release state (was: `done` or nothing, so a
 * mission waiting on G3 kept whatever label an earlier run left).
 */
export function missionStatusLabelForReleaseFlow(
  outcome: ReleaseFlowResumeOutcome | undefined,
): string {
  if (outcome === undefined) return "done";
  switch (outcome.kind) {
    case "skipped_no_release_config":
      return "done";
    case "waiting_gate_g3":
      return "waiting_gate:g3";
    // CI gate (dogfood 2026-10-01): not finished is non-terminal, like waiting on G3.
    case "waiting_ci":
      return "waiting_ci";
    case "release_ci_failed":
      return "blocked:release_ci_failed";
    case "release_ci_not_passed":
      return "blocked:release_ci_not_passed";
    case "waiting_review_report":
    case "release_flow_blocked":
      return "blocked:release_flow";
    case "deploy_not_configured":
      return "done_without_deploy";
    case "released":
    case "already_deployed":
      return "released";
    case "deploy_blocked":
      return "blocked:deploy";
    case "deploy_smoke_failed":
      return "blocked:deploy_smoke_failed";
    case "release_g3_invalid_after_deploy":
      return "blocked:release_g3_invalid";
    case "release_unverified_legacy":
      return "blocked:release_unverified_legacy";
    case "release_deploy_outcome_unknown":
      return "blocked:deploy_outcome_unknown";
    default: {
      const unhandled: never = outcome;
      return unhandled;
    }
  }
}

/**
 * Operator-facing sentence for one resume outcome, or `undefined` when the
 * outcome needs no attention (`already_deployed` — Bug O's idempotent re-run
 * — and `skipped_no_release_config`). Narrating a no-op on every `run` would
 * train the reader to skip these lines, which is how a real warning gets
 * missed.
 */
export function describeReleaseFlowOutcome(
  outcome: ReleaseFlowResumeOutcome,
  /** Printed in the `approve g3` command (dogfood Beta 9 F4); absent ⇒ `<id>`. */
  missionId = "<id>",
): string | undefined {
  switch (outcome.kind) {
    case "waiting_review_report":
      return `Release flow đang chờ review report: ${outcome.reason}`;
    case "waiting_gate_g3": {
      const why = outcome.reason === undefined ? "" : ` ${outcome.reason}`;
      const noDeploy = outcome.deployConfigured
        ? ""
        : " (config.release chưa có deploy_recipe ⇒ duyệt G3 KHÔNG deploy gì; G3 chỉ ghi nhận duyệt, MR vẫn merge tay)";
      return `Release flow đang chờ duyệt gate G3 (request ${outcome.g3RequestId})${noDeploy} — duyệt bằng \`avatar autopilot approve g3 --mission ${missionId} --request ${outcome.g3RequestId}\`.${why}`;
    }
    case "release_flow_blocked":
      return `Release flow bị chặn: ${outcome.reason}`;
    case "waiting_ci":
      return `Release flow đang chờ CI: ${outcome.reason}`;
    case "release_ci_failed":
      return `Release flow bị chặn — CI thất bại: ${outcome.reason}`;
    case "release_ci_not_passed":
      return `Release flow bị chặn — CI chưa pass: ${outcome.reason}`;
    case "deploy_blocked":
      return `Deploy bị chặn: ${outcome.reason}`;
    case "deploy_smoke_failed":
      return `Deploy đã chạy (exit 0) nhưng smoke thất bại: ${outcome.reason} — chạy lại \`run\` chỉ chạy lại smoke, KHÔNG deploy lần hai.`;
    case "release_unverified_legacy":
      return `Release chưa được xác minh: ${outcome.reason}`;
    case "release_g3_invalid_after_deploy":
      return `G3 không còn hợp lệ sau khi đã deploy: ${outcome.reason}`;
    case "release_deploy_outcome_unknown":
      return `Dừng chờ người — ${outcome.reason}`;
    case "deploy_not_configured":
      return "Mission xong — KHÔNG deploy (config.release chưa có deploy_recipe). MR candidate vẫn merge tay theo quy trình của repo.";
    case "released":
      return "Đã deploy + smoke pass. Rollout SHA: không xác minh (v1 chưa có probe bản đang chạy).";
    // Compile-time exhaustiveness: a NEW outcome kind must be handled here or
    // `tsc` fails — a real state reading as "nothing to see" is the bug class
    // this file exists to stop (P20/P21/P24).
    case "already_deployed":
    case "skipped_no_release_config":
      return undefined;
    default: {
      const unhandled: never = outcome;
      void unhandled;
      return undefined;
    }
  }
}

/**
 * Journals the resume outcome so it survives the terminal. Committed for
 * EVERY outcome that carries information — including the ones
 * `describeReleaseFlowOutcome` stays quiet about at the console, because
 * "the flow ran and had nothing to do" is itself worth being able to prove
 * later, and its absence is what a flow that never ran looks like.
 */
export async function journalReleaseFlowOutcome(
  store: Pick<MissionStore, "commit">,
  outcome: ReleaseFlowResumeOutcome,
): Promise<void> {
  if (outcome.kind === "skipped_no_release_config") return;
  const operationId = `release-flow-resume-${randomUUID()}`;
  const reason = (outcome as { reason?: string }).reason;
  await store.commit([
    {
      event_id: randomUUID(),
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        operation_kind: "release_flow_resume",
        outcome: outcome.kind,
        ...(reason === undefined ? {} : { reason }),
      },
    },
  ]);
}
